import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';

import { Fonts } from '@/constants/theme';
import { fetchAssignmentsForSchool, type AssignmentListItem } from '@/lib/assignments-admin';
import {
  createClass,
  fetchClassesForSchool,
  type ClassListItem,
  updateClass,
} from '@/lib/classes-admin';
import { useAuth } from '@/providers/auth-provider';

type ModalMode = 'create' | 'edit';

export default function AdminClassesScreen() {
  const { profile } = useAuth();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 960;
  const [classes, setClasses] = useState<ClassListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>('create');
  const [selectedClass, setSelectedClass] = useState<ClassListItem | null>(null);
  const [assignmentLinks, setAssignmentLinks] = useState<AssignmentListItem[]>([]);
  const [name, setName] = useState('');
  const [section, setSection] = useState('');
  const [academicYear, setAcademicYear] = useState('');
  const modalTitle = useMemo(
    () => (modalMode === 'create' ? 'Add class' : 'Edit class'),
    [modalMode]
  );

  const loadClasses = useCallback(async () => {
    if (!profile?.school_id) {
      setLoading(false);
      return;
    }

    try {
      const [rows, links] = await Promise.all([
        fetchClassesForSchool(profile.school_id),
        fetchAssignmentsForSchool(profile.school_id),
      ]);
      setClasses(rows);
      setAssignmentLinks(links);
    } catch (error) {
      Alert.alert('Unable to load classes', getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [profile?.school_id]);

  useEffect(() => {
    void loadClasses();
  }, [loadClasses]);

  const classStats = useMemo(() => {
    const total = classes.length;
    const linked = new Set(assignmentLinks.map((item) => item.class_id)).size;
    const open = total - linked;

    return [
      { label: 'Total', value: String(total) },
      { label: 'Linked', value: String(linked) },
      { label: 'Open', value: String(open) },
    ];
  }, [assignmentLinks, classes]);

  const linkedClassIds = useMemo(
    () => new Set(assignmentLinks.map((item) => item.class_id)),
    [assignmentLinks]
  );

  const resetForm = () => {
    setSelectedClass(null);
    setName('');
    setSection('');
    setAcademicYear('');
    setModalMode('create');
  };

  function openCreateModal() {
    resetForm();
    setAcademicYear(getDefaultAcademicYear());
    setModalVisible(true);
  }

  function openEditModal(item: ClassListItem) {
    setSelectedClass(item);
    setModalMode('edit');
    setName(item.name);
    setSection(item.section ?? '');
    setAcademicYear(item.academic_year);
    setModalVisible(true);
  }

  const closeModal = () => {
    setModalVisible(false);
    resetForm();
  };

  const handleCreateClass = async () => {
    if (!profile?.school_id) {
      Alert.alert('School missing', 'Your account is missing school access.');
      return;
    }

    if (!name.trim() || !academicYear.trim()) {
      Alert.alert('Missing details', 'Enter class name and academic year.');
      return;
    }

    setSubmitting(true);

    try {
      await createClass({
        academicYear,
        name,
        schoolId: profile.school_id,
        section,
      });

      closeModal();
      await loadClasses();
      Alert.alert('Class added', 'The class was created successfully.');
    } catch (error) {
      Alert.alert('Create failed', getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditClass = async () => {
    if (!profile?.school_id || !selectedClass) {
      Alert.alert('Class missing', 'Select a class first.');
      return;
    }

    if (!name.trim() || !academicYear.trim()) {
      Alert.alert('Missing details', 'Enter class name and academic year.');
      return;
    }

    setSubmitting(true);

    try {
      await updateClass({
        academicYear,
        classId: selectedClass.id,
        name,
        schoolId: profile.school_id,
        section,
      });

      closeModal();
      await loadClasses();
      Alert.alert('Class updated', 'The class details were updated.');
    } catch (error) {
      Alert.alert('Update failed', getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = () => {
    if (modalMode === 'create') {
      void handleCreateClass();
      return;
    }

    void handleEditClass();
  };

  const desktopModalSheet = (
    <Modal
      animationType="fade"
      transparent
      visible={modalVisible}
      onRequestClose={closeModal}>
      <View style={styles.desktopModalBackdrop}>
        <Pressable style={StyleSheet.absoluteFill} onPress={closeModal} />
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.desktopModalKeyboard}>
          <View style={styles.desktopModalShell}>
            <View style={styles.desktopModalCard}>
              <ScrollView
                contentContainerStyle={styles.desktopModalScroll}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator={false}>
                <View style={styles.desktopModalHeader}>
                  <View>
                    <Text style={styles.desktopModalHeaderLabel}>
                      {modalMode === 'create' ? 'New class' : 'Edit class'}
                    </Text>
                    <Text style={styles.desktopModalHeaderValue}>
                      {modalMode === 'create' ? 'Add a class' : 'Update the class'}
                    </Text>
                    <Text style={styles.desktopModalHeaderSubtext}>
                      Manage the class name, section, and academic year in one place.
                    </Text>
                  </View>
                  <Pressable onPress={closeModal} style={styles.desktopModalClose}>
                    <MaterialIcons name="close" size={18} color="#163828" />
                  </Pressable>
                </View>

                <View style={styles.desktopModalForm}>
                  <InputField
                    autoCapitalize="words"
                    label="CLASS NAME"
                    onChangeText={setName}
                    placeholder="e.g. Grade 10"
                    value={name}
                  />

                  <View style={styles.desktopModalRow}>
                    <View style={{ flex: 1 }}>
                      <InputField
                        autoCapitalize="characters"
                        label="SECTION"
                        onChangeText={setSection}
                        placeholder="e.g. A"
                        value={section}
                      />
                    </View>
                    <View style={{ flex: 1.2 }}>
                      <InputField
                        label="ACADEMIC YEAR"
                        onChangeText={setAcademicYear}
                        placeholder="2026-2027"
                        value={academicYear}
                      />
                    </View>
                  </View>
                </View>

                <View style={styles.desktopModalActions}>
                  <Pressable onPress={closeModal} style={styles.desktopModalCancel}>
                    <Text style={styles.desktopModalCancelText}>Cancel</Text>
                  </Pressable>
                  <Pressable onPress={handleSubmit} style={styles.desktopModalSave}>
                    {submitting ? (
                      <ActivityIndicator color="#FFFFFF" />
                    ) : (
                      <Text style={styles.desktopModalSaveText}>
                        {modalMode === 'create' ? 'Save class' : 'Update class'}
                      </Text>
                    )}
                  </Pressable>
                </View>
              </ScrollView>
            </View>
          </View>
        </KeyboardAvoidingView>
      </View>
    </Modal>
  );

  const mobileModalSheet = (
    <Modal animationType="slide" transparent visible={modalVisible} onRequestClose={closeModal}>
      <View style={styles.modalBackdrop}>
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.modalKeyboard}>
          <View style={styles.modalCard}>
            <ScrollView
              contentContainerStyle={styles.modalScrollContent}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator={false}>
              <View style={styles.modalHandle} />
              <Text style={styles.modalTitle}>{modalTitle}</Text>
              <Text style={styles.modalSubtitle}>
                Keep it short and readable for mobile management.
              </Text>

              <InputField
                autoCapitalize="words"
                label="CLASS NAME"
                onChangeText={setName}
                placeholder="e.g. Grade 10"
                value={name}
              />
              <View style={styles.modalRow}>
                <View style={{ flex: 1 }}>
                  <InputField
                    autoCapitalize="characters"
                    label="SECTION"
                    onChangeText={setSection}
                    placeholder="e.g. A"
                    value={section}
                  />
                </View>
                <View style={{ flex: 1.5 }}>
                  <InputField
                    label="ACADEMIC YEAR"
                    onChangeText={setAcademicYear}
                    placeholder="2026-2027"
                    value={academicYear}
                  />
                </View>
              </View>

              <View style={styles.modalActions}>
                <Pressable onPress={closeModal} style={styles.cancelBtn}>
                  <Text style={styles.cancelBtnText}>Cancel</Text>
                </Pressable>
                <Pressable onPress={handleSubmit} style={styles.saveBtn}>
                  {submitting ? (
                    <ActivityIndicator color="#FFFFFF" />
                  ) : (
                    <Text style={styles.saveBtnText}>
                      {modalMode === 'create' ? 'Save class' : 'Update class'}
                    </Text>
                  )}
                </Pressable>
              </View>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </View>
    </Modal>
  );

  const desktopStats = [
    { label: 'Total', value: String(classStats[0]?.value ?? '0') },
    { label: 'Linked', value: String(classStats[1]?.value ?? '0') },
    { label: 'Open', value: String(classStats[2]?.value ?? '0') },
  ];

  if (isDesktop) {
    return (
      <>
      <View style={styles.desktopPage}>
        <ScrollView
          contentContainerStyle={styles.desktopScroll}
          showsVerticalScrollIndicator={false}
          style={styles.desktopScrollView}>
          <View style={styles.desktopHero}>
            <View style={styles.desktopHeroTop}>
              <View style={styles.desktopHeroCopy}>
                <Text style={styles.desktopEyebrow}>Admin / Classes</Text>
                <Text style={styles.desktopTitle}>Class structure</Text>
                <Text style={styles.desktopSubtitle}>
                  Keep sections, academic years, and teacher links organized from one workspace.
                </Text>
              </View>

              <Pressable onPress={openCreateModal} style={styles.desktopHeaderBtn}>
                <MaterialIcons name="add" size={20} color="#F4EFE6" />
                <Text style={styles.desktopHeaderBtnText}>Add class</Text>
              </Pressable>
            </View>

            <View style={styles.desktopHeroPanel}>
              <View style={styles.desktopHeroPanelMain}>
                <Text style={styles.desktopHeroDate}>Workspace overview</Text>
                <Text style={styles.desktopHeroHeadline}>Classes, links, and yearly setup.</Text>
                <Text style={styles.desktopHeroBody}>
                  Use the desktop layout to scan the class roster, track teacher assignment
                  coverage, and add or edit records faster.
                </Text>
              </View>

              <View style={styles.desktopStatsGrid}>
                {desktopStats.map((item, index) => (
                  <View
                    key={item.label}
                    style={[styles.desktopStatCard, index > 0 ? styles.desktopStatDivider : null]}>
                    <Text style={styles.desktopStatValue}>{item.value}</Text>
                    <Text style={styles.desktopStatLabel}>{item.label}</Text>
                  </View>
                ))}
              </View>
            </View>
          </View>

          <View style={styles.desktopGrid}>
            <View style={styles.desktopColumnLarge}>
              <View style={styles.desktopSection}>
                <View style={styles.sectionHeadDesktop}>
                  <View>
                    <Text style={styles.sectionTitleDesktop}>All classes</Text>
                    <Text style={styles.sectionCaptionDesktop}>
                      Select a row to edit class details.
                    </Text>
                  </View>
                  <Pressable onPress={openCreateModal}>
                    <Text style={styles.linkTextDesktop}>Add class</Text>
                  </Pressable>
                </View>

                {loading ? (
                  <View style={styles.desktopLoadingWrap}>
                    <ActivityIndicator color="#163828" />
                  </View>
                ) : classes.length === 0 ? (
                  <View style={styles.desktopEmptyState}>
                    <View style={styles.desktopEmptyIcon}>
                      <MaterialIcons name="domain" size={26} color="#163828" />
                    </View>
                    <Text style={styles.desktopEmptyTitle}>No classes found</Text>
                    <Text style={styles.desktopEmptyText}>
                      Create the first class to start organizing the school year.
                    </Text>
                    <Pressable onPress={openCreateModal} style={styles.desktopEmptyButton}>
                      <Text style={styles.desktopEmptyButtonText}>Create class</Text>
                    </Pressable>
                  </View>
                ) : (
                  <View style={styles.desktopListBlock}>
                    {classes.map((item, index) => {
                      const isLinked = linkedClassIds.has(item.id);

                      return (
                        <Pressable
                          key={item.id}
                          onPress={() => openEditModal(item)}
                          style={[
                            styles.desktopListRow,
                            index < classes.length - 1 ? styles.desktopListDivider : null,
                          ]}>
                          <View style={styles.desktopRowIndex}>
                            <Text style={styles.desktopRowIndexText}>
                              {item.name.slice(0, 1).toUpperCase()}
                            </Text>
                          </View>

                          <View style={styles.desktopRowCopy}>
                            <Text style={styles.desktopRowTitle} numberOfLines={1}>
                              {item.name}
                              {item.section?.trim() ? ` - ${item.section}` : ''}
                            </Text>
                            <Text style={styles.desktopRowMeta} numberOfLines={1}>
                              {item.academic_year}
                            </Text>
                          </View>

                          <View style={styles.desktopRowRight}>
                            <View
                              style={[
                                styles.desktopRowTag,
                                { backgroundColor: isLinked ? '#E8F5E9' : '#EEF4EF' },
                              ]}>
                              <Text
                                style={[
                                  styles.desktopRowTagText,
                                  { color: isLinked ? '#1B5E20' : '#5D7261' },
                                ]}>
                                {isLinked ? 'Linked' : 'Open'}
                              </Text>
                            </View>
                            <Text style={styles.desktopRowCount}>
                              {isLinked ? 'Assignment active' : 'No teacher linked'}
                            </Text>
                          </View>
                        </Pressable>
                      );
                    })}
                  </View>
                )}
              </View>
            </View>

            <View style={styles.desktopColumnSide}>
              <View style={styles.desktopSection}>
                <View style={styles.sectionHeadDesktop}>
                  <View>
                    <Text style={styles.sectionTitleDesktop}>Teacher links</Text>
                    <Text style={styles.sectionCaptionDesktop}>
                      Classes already connected to teachers.
                    </Text>
                  </View>
                </View>

                {loading ? (
                  <View style={styles.desktopLoadingWrap}>
                    <ActivityIndicator color="#163828" />
                  </View>
                ) : assignmentLinks.length === 0 ? (
                  <View style={styles.desktopEmptyState}>
                    <Text style={styles.desktopEmptyTitle}>No teacher links yet</Text>
                    <Text style={styles.desktopEmptyText}>
                      Create assignments to connect a teacher to a class.
                    </Text>
                  </View>
                ) : (
                  <View style={styles.desktopListBlock}>
                    {assignmentLinks.map((item, index) => (
                      <View
                        key={item.id}
                        style={[
                          styles.desktopListRow,
                          index < assignmentLinks.length - 1 ? styles.desktopListDivider : null,
                        ]}>
                        <View style={styles.desktopRowIndex}>
                          <Text style={styles.desktopRowIndexText}>
                            {item.class_label.slice(0, 1).toUpperCase()}
                          </Text>
                        </View>

                        <View style={styles.desktopRowCopy}>
                          <Text style={styles.desktopRowTitle} numberOfLines={1}>
                            {item.class_label}
                          </Text>
                          <Text style={styles.desktopRowMeta} numberOfLines={1}>
                            {item.subject_label}
                          </Text>
                        </View>

                        <View style={styles.desktopRowRight}>
                          <View style={styles.desktopRowTag}>
                            <Text style={styles.desktopRowTagText}>Linked</Text>
                          </View>
                          <Text style={styles.desktopRowCount}>{item.teacher_label}</Text>
                        </View>
                      </View>
                    ))}
                  </View>
                )}
              </View>
            </View>
          </View>
        </ScrollView>
      </View>
      {desktopModalSheet}
      </>
    );
  }

  return (
    <View style={styles.container}>
      <SafeAreaView style={styles.safeArea}>
        <ScrollView
          contentContainerStyle={[
            styles.scrollContent,
            { paddingBottom: Math.max(insets.bottom, 20) + 88 },
          ]}
        showsVerticalScrollIndicator={false}>
          <View style={styles.heroShell}>
            <View style={styles.heroPanel}>
              <View style={styles.heroHeaderRow}>
                <Text style={styles.heroKicker}>Admin / Classes</Text>
                <Pressable
                  onPress={openCreateModal}
                  style={({ pressed }) => [styles.heroButton, pressed && styles.pressed]}>
                  <MaterialIcons name="add" size={18} color="#163828" />
                  <Text style={styles.heroButtonText}>Add class</Text>
                </Pressable>
              </View>

              <View style={styles.heroBody}>
                <Text style={styles.heroTitle}>Manage class structure from one screen.</Text>
                <Text style={styles.heroSubtitle}>
                  Keep sections, years, and teacher links organized with a clean mobile surface.
                </Text>
              </View>

              <View style={styles.heroStatsRow}>
                {classStats.map((item, index) => (
                  <View
                    key={item.label}
                    style={[styles.heroStat, index > 0 ? styles.heroStatDivider : null]}>
                    <Text style={styles.heroStatValue}>{item.value}</Text>
                    <Text style={styles.heroStatLabel}>{item.label}</Text>
                  </View>
                ))}
              </View>
            </View>
          </View>

          <View style={styles.surface}>
            <View style={styles.sectionBlock}>
              <View style={styles.sectionHead}>
                <Text style={styles.sectionTitle}>All Classes</Text>
                <Pressable onPress={openCreateModal}>
                  <Text style={styles.linkText}>Add class</Text>
                </Pressable>
              </View>

              {loading ? (
                <View style={styles.loadingWrap}>
                  <ActivityIndicator color="#163828" />
                </View>
              ) : classes.length === 0 ? (
                <View style={styles.emptyState}>
                  <View style={styles.emptyIcon}>
                    <MaterialIcons name="domain" size={28} color="#163828" />
                  </View>
                  <Text style={styles.emptyTitle}>No classes found</Text>
                  <Text style={styles.emptyText}>
                    Create the first class to start organizing the school year.
                  </Text>
                  <Pressable onPress={openCreateModal} style={styles.emptyButton}>
                    <Text style={styles.emptyButtonText}>Create class</Text>
                  </Pressable>
                </View>
              ) : (
                <View style={styles.listBlock}>
                  {classes.map((item, index) => (
                    <Pressable
                      key={item.id}
                      onPress={() => openEditModal(item)}
                      style={[
                        styles.listRow,
                        index < classes.length - 1 ? styles.listDivider : null,
                      ]}>
                      <View style={styles.rowIndex}>
                        <Text style={styles.rowIndexText}>
                          {item.name.slice(0, 1).toUpperCase()}
                        </Text>
                      </View>

                      <View style={styles.rowCopy}>
                        <Text style={styles.rowTitle} numberOfLines={1}>
                          {item.name}
                          {item.section?.trim() ? ` - ${item.section}` : ''}
                        </Text>
                        <Text style={styles.rowMeta} numberOfLines={1}>
                          {item.academic_year}
                        </Text>
                      </View>

                      <View style={styles.rowRight}>
                        <View style={styles.rowTag}>
                          <Text style={styles.rowTagText}>
                            {linkedClassIds.has(item.id) ? 'Linked' : 'Open'}
                          </Text>
                        </View>
                        <Text style={styles.rowCount}>
                          {linkedClassIds.has(item.id) ? 'Assignment active' : 'No teacher linked'}
                        </Text>
                      </View>
                    </Pressable>
                  ))}
                </View>
              )}
            </View>

            <View style={styles.sectionBlock}>
              <View style={styles.sectionHead}>
                <Text style={styles.sectionTitle}>Teacher Links</Text>
                <Text style={styles.sectionCaption}>Classes already connected to teachers.</Text>
              </View>

              {loading ? (
                <View style={styles.loadingWrap}>
                  <ActivityIndicator color="#163828" />
                </View>
              ) : assignmentLinks.length === 0 ? (
                <View style={styles.emptyState}>
                  <Text style={styles.emptyTitle}>No teacher links yet</Text>
                  <Text style={styles.emptyText}>
                    Create assignments to connect a teacher to a class.
                  </Text>
                </View>
              ) : (
                <View style={styles.listBlock}>
                  {assignmentLinks.map((item, index) => (
                    <Pressable
                      key={item.id}
                      style={[
                        styles.listRow,
                        index < assignmentLinks.length - 1 ? styles.listDivider : null,
                      ]}>
                      <View style={styles.rowIndex}>
                        <Text style={styles.rowIndexText}>
                          {item.class_label.slice(0, 1).toUpperCase()}
                        </Text>
                      </View>

                      <View style={styles.rowCopy}>
                        <Text style={styles.rowTitle} numberOfLines={1}>
                          {item.class_label}
                        </Text>
                        <Text style={styles.rowMeta} numberOfLines={1}>
                          {item.subject_label}
                        </Text>
                      </View>

                      <View style={styles.rowRight}>
                        <View style={styles.rowTag}>
                          <Text style={styles.rowTagText}>Linked</Text>
                        </View>
                        <Text style={styles.rowCount}>{item.teacher_label}</Text>
                      </View>
                    </Pressable>
                  ))}
                </View>
              )}
            </View>
          </View>
        </ScrollView>
      </SafeAreaView>

      {mobileModalSheet}
    </View>
  );
}

function InputField(props: React.ComponentProps<typeof TextInput> & { label: string }) {
  return (
    <View style={styles.field}>
      <Text style={styles.fieldLabel}>{props.label}</Text>
      <TextInput placeholderTextColor="#A8B9AA" style={styles.input} {...props} />
    </View>
  );
}

function getDefaultAcademicYear() {
  const currentYear = new Date().getFullYear();
  return `${currentYear}-${currentYear + 1}`;
}

function getErrorMessage(error: unknown) {
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}

const styles = StyleSheet.create({
  desktopPage: {
    flex: 1,
    backgroundColor: '#F4EFE6',
  },
  desktopScrollView: {
    flex: 1,
  },
  desktopScroll: {
    padding: 28,
    gap: 24,
  },
  desktopHero: {
    gap: 18,
  },
  desktopHeroTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 20,
  },
  desktopHeroCopy: {
    gap: 6,
    flex: 1,
    minWidth: 0,
  },
  desktopEyebrow: {
    color: '#6F7D71',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 1.2,
    fontWeight: '800',
  },
  desktopTitle: {
    color: '#163828',
    fontSize: 34,
    lineHeight: 40,
    fontFamily: Fonts.rounded,
    fontWeight: '900',
  },
  desktopSubtitle: {
    color: '#6F7D71',
    fontSize: 14,
    lineHeight: 20,
  },
  desktopHeaderBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: '#163828',
    borderRadius: 18,
    paddingHorizontal: 14,
    paddingVertical: 12,
    minWidth: 150,
  },
  desktopHeaderBtnText: {
    color: '#F7F4EE',
    fontSize: 13,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  desktopHeroPanel: {
    backgroundColor: '#103427',
    borderRadius: 28,
    padding: 22,
    gap: 18,
    borderWidth: 1,
    borderColor: 'rgba(244, 239, 230, 0.09)',
  },
  desktopHeroPanelMain: {
    gap: 8,
    maxWidth: 560,
  },
  desktopHeroDate: {
    color: '#BBD0C2',
    fontSize: 11,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 1.1,
  },
  desktopHeroHeadline: {
    color: '#F7F4EE',
    fontSize: 28,
    lineHeight: 34,
    fontFamily: Fonts.rounded,
    fontWeight: '900',
  },
  desktopHeroBody: {
    color: '#C4D3C9',
    fontSize: 14,
    lineHeight: 21,
    maxWidth: 620,
  },
  desktopStatsGrid: {
    flexDirection: 'row',
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: 'rgba(244, 239, 230, 0.12)',
  },
  desktopStatCard: {
    flex: 1,
    paddingTop: 4,
    paddingBottom: 14,
  },
  desktopStatDivider: {
    paddingLeft: 14,
    marginLeft: 14,
    borderLeftWidth: 1,
    borderLeftColor: 'rgba(244, 239, 230, 0.12)',
  },
  desktopStatValue: {
    color: '#F7F4EE',
    fontSize: 24,
    lineHeight: 28,
    fontFamily: Fonts.rounded,
    fontWeight: '800',
  },
  desktopStatLabel: {
    color: '#9EB5A7',
    fontSize: 12,
    marginTop: 4,
  },
  desktopGrid: {
    flexDirection: 'row',
    gap: 20,
    alignItems: 'flex-start',
  },
  desktopColumnLarge: {
    flex: 1.4,
    gap: 20,
    minWidth: 0,
  },
  desktopColumnSide: {
    flex: 0.9,
    gap: 20,
    minWidth: 0,
  },
  desktopSection: {
    gap: 14,
  },
  sectionHeadDesktop: {
    gap: 4,
  },
  sectionTitleDesktop: {
    color: '#163828',
    fontSize: 20,
    lineHeight: 24,
    fontFamily: Fonts.rounded,
    fontWeight: '800',
  },
  sectionCaptionDesktop: {
    color: '#6F7D71',
    fontSize: 13,
    lineHeight: 18,
  },
  linkTextDesktop: {
    color: '#47624F',
    fontSize: 13,
    fontWeight: '700',
  },
  desktopListBlock: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    overflow: 'hidden',
  },
  desktopListRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 14,
  },
  desktopListDivider: {
    borderBottomWidth: 1,
    borderBottomColor: '#EEF2EA',
  },
  desktopRowIndex: {
    width: 38,
    height: 38,
    borderRadius: 12,
    backgroundColor: '#F0F5F0',
    alignItems: 'center',
    justifyContent: 'center',
  },
  desktopRowIndexText: {
    color: '#163828',
    fontSize: 13,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  desktopRowCopy: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  desktopRowTitle: {
    color: '#163828',
    fontSize: 15,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  desktopRowMeta: {
    color: '#6F7D71',
    fontSize: 12,
    lineHeight: 16,
  },
  desktopRowRight: {
    alignItems: 'flex-end',
    gap: 4,
  },
  desktopRowTag: {
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  desktopRowTagText: {
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
  },
  desktopRowCount: {
    color: '#6F7D71',
    fontSize: 11,
    fontWeight: '700',
  },
  desktopLoadingWrap: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    paddingVertical: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  desktopEmptyState: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    padding: 18,
    alignItems: 'center',
    gap: 8,
  },
  desktopEmptyIcon: {
    width: 48,
    height: 48,
    borderRadius: 16,
    backgroundColor: '#EEF5EE',
    alignItems: 'center',
    justifyContent: 'center',
  },
  desktopEmptyTitle: {
    color: '#163828',
    fontSize: 15,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  desktopEmptyText: {
    color: '#6F7D71',
    fontSize: 13,
    lineHeight: 19,
    textAlign: 'center',
  },
  desktopEmptyButton: {
    alignSelf: 'stretch',
    minHeight: 44,
    borderRadius: 14,
    backgroundColor: '#163828',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 4,
  },
  desktopEmptyButtonText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  desktopModalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(13, 22, 16, 0.62)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  desktopModalKeyboard: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  desktopModalShell: {
    width: '100%',
    maxWidth: 760,
    borderRadius: 30,
    overflow: 'hidden',
    backgroundColor: '#FFFFFF',
    shadowColor: '#0E2817',
    shadowOpacity: 0.22,
    shadowRadius: 28,
    shadowOffset: { width: 0, height: 18 },
    elevation: 24,
  },
  desktopModalCard: {
    backgroundColor: '#FDFBF6',
    paddingHorizontal: 28,
    paddingVertical: 24,
  },
  desktopModalScroll: {
    gap: 18,
    paddingBottom: 4,
  },
  desktopModalHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
  },
  desktopModalHeaderLabel: {
    color: '#6F7D71',
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 1,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
  },
  desktopModalHeaderValue: {
    color: '#163828',
    fontSize: 22,
    lineHeight: 28,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
    marginTop: 2,
  },
  desktopModalHeaderSubtext: {
    color: '#6F7D71',
    fontSize: 13,
    lineHeight: 18,
    marginTop: 4,
    maxWidth: 520,
  },
  desktopModalClose: {
    width: 40,
    height: 40,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E7EEE7',
  },
  desktopModalForm: {
    gap: 16,
  },
  desktopModalRow: {
    flexDirection: 'row',
    gap: 12,
  },
  desktopModalActions: {
    flexDirection: 'row',
    gap: 12,
    paddingTop: 8,
  },
  desktopModalCancel: {
    flex: 1,
    minHeight: 52,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F5F7F5',
    borderWidth: 1,
    borderColor: '#E6ECE6',
  },
  desktopModalCancelText: {
    color: '#3C4A3E',
    fontSize: 13,
    fontWeight: '800',
  },
  desktopModalSave: {
    flex: 1.4,
    minHeight: 52,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#163828',
  },
  desktopModalSaveText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  container: {
    flex: 1,
    backgroundColor: '#F4EFE6',
  },
  safeArea: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 18,
    paddingTop: 10,
    gap: 18,
  },
  heroShell: {
    gap: 18,
  },
  heroTopRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 14,
  },
  identityRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: 16,
    backgroundColor: '#163828',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    color: '#F4EFE6',
    fontSize: 15,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  identityText: {
    flex: 1,
    gap: 2,
  },
  eyebrow: {
    color: '#6F7D71',
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
  },
  name: {
    color: '#163828',
    fontSize: 18,
    lineHeight: 22,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  headerIconBtn: {
    width: 46,
    height: 46,
    borderRadius: 16,
    backgroundColor: '#163828',
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroPanel: {
    backgroundColor: '#163828',
    borderRadius: 28,
    padding: 18,
    gap: 18,
  },
  heroHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  heroBody: {
    gap: 8,
  },
  heroKicker: {
    color: '#B9CFBE',
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
  },
  heroTitle: {
    color: '#FFFFFF',
    fontSize: 27,
    lineHeight: 33,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  heroSubtitle: {
    color: '#D4E4D7',
    fontSize: 14,
    lineHeight: 20,
  },
  heroButton: {
    alignSelf: 'flex-start',
    backgroundColor: '#F4EFE6',
    borderRadius: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  heroButtonText: {
    color: '#163828',
    fontSize: 13,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  heroStatsRow: {
    flexDirection: 'row',
    borderRadius: 20,
    overflow: 'hidden',
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
  },
  heroStat: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 10,
    gap: 3,
  },
  heroStatDivider: {
    borderLeftWidth: 1,
    borderLeftColor: 'rgba(255, 255, 255, 0.12)',
  },
  heroStatValue: {
    color: '#FFFFFF',
    fontSize: 20,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  heroStatLabel: {
    color: '#B9CFBE',
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 1.1,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
  },
  surface: {
    gap: 18,
  },
  sectionBlock: {
    gap: 12,
  },
  sectionHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  sectionTitle: {
    color: '#163828',
    fontSize: 18,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  sectionCaption: {
    color: '#6F7D71',
    fontSize: 12,
    lineHeight: 16,
    flexShrink: 1,
    textAlign: 'right',
  },
  linkText: {
    color: '#6F7D71',
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 1.1,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
  },
  actionRail: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    overflow: 'hidden',
  },
  actionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 14,
  },
  actionDivider: {
    borderBottomWidth: 1,
    borderBottomColor: '#E8EFE5',
  },
  actionIconWrap: {
    width: 42,
    height: 42,
    borderRadius: 14,
    backgroundColor: '#EEF5EE',
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionCopy: {
    flex: 1,
    gap: 2,
  },
  actionLabel: {
    color: '#163828',
    fontSize: 15,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  actionSubtitle: {
    color: '#6F7D71',
    fontSize: 12,
    lineHeight: 16,
  },
  searchBox: {
    height: 50,
    borderRadius: 18,
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  searchInput: {
    flex: 1,
    height: '100%',
    fontSize: 15,
    color: '#1C1C1E',
  },
  filterRow: {
    gap: 8,
    paddingRight: 4,
  },
  filterChip: {
    height: 36,
    paddingHorizontal: 14,
    borderRadius: 999,
    backgroundColor: '#EEF4EF',
    justifyContent: 'center',
  },
  filterChipActive: {
    backgroundColor: '#163828',
  },
  filterText: {
    color: '#5D7261',
    fontSize: 12,
    fontWeight: '700',
  },
  filterTextActive: {
    color: '#FFFFFF',
  },
  snapshotPanel: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    padding: 16,
    gap: 14,
  },
  snapshotMain: {
    gap: 6,
  },
  snapshotLabel: {
    color: '#6F7D71',
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 1.1,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
  },
  snapshotValue: {
    color: '#163828',
    fontSize: 20,
    lineHeight: 24,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  snapshotMeta: {
    color: '#6F7D71',
    fontSize: 13,
    lineHeight: 19,
  },
  snapshotButton: {
    alignSelf: 'flex-start',
    backgroundColor: '#163828',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 14,
  },
  snapshotButtonText: {
    color: '#F4EFE6',
    fontSize: 13,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  listBlock: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    overflow: 'hidden',
  },
  listRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 14,
  },
  listDivider: {
    borderBottomWidth: 1,
    borderBottomColor: '#EEF2EA',
  },
  rowIndex: {
    width: 38,
    height: 38,
    borderRadius: 12,
    backgroundColor: '#F0F5F0',
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowIndexText: {
    color: '#163828',
    fontSize: 13,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  rowCopy: {
    flex: 1,
    gap: 2,
  },
  rowTitle: {
    color: '#163828',
    fontSize: 15,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  rowMeta: {
    color: '#6F7D71',
    fontSize: 12,
    lineHeight: 16,
  },
  rowRight: {
    alignItems: 'flex-end',
    gap: 4,
  },
  rowTag: {
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: '#E8F5E9',
  },
  rowTagText: {
    color: '#1B5E20',
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
  },
  rowCount: {
    color: '#6F7D71',
    fontSize: 11,
    fontWeight: '700',
  },
  loadingWrap: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    paddingVertical: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyState: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    padding: 18,
    alignItems: 'center',
    gap: 8,
  },
  emptyIcon: {
    width: 48,
    height: 48,
    borderRadius: 16,
    backgroundColor: '#EEF5EE',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTitle: {
    color: '#163828',
    fontSize: 15,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  emptyText: {
    color: '#6F7D71',
    fontSize: 13,
    lineHeight: 19,
    textAlign: 'center',
  },
  emptyButton: {
    alignSelf: 'stretch',
    minHeight: 44,
    borderRadius: 14,
    backgroundColor: '#163828',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyButtonText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'flex-end',
  },
  modalKeyboard: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  modalCard: {
    borderTopLeftRadius: 30,
    borderTopRightRadius: 30,
    backgroundColor: '#FFFFFF',
    padding: 22,
    paddingBottom: 40,
    gap: 16,
    maxHeight: '88%',
  },
  modalScrollContent: {
    gap: 16,
    paddingBottom: 4,
  },
  modalHandle: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: '#EDF2EE',
    alignSelf: 'center',
  },
  modalTitle: {
    color: '#163828',
    fontSize: 20,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  modalSubtitle: {
    color: '#6A8570',
    fontSize: 13,
    lineHeight: 19,
    marginTop: -8,
  },
  modalRow: {
    flexDirection: 'row',
    gap: 12,
  },
  field: {
    gap: 8,
  },
  fieldLabel: {
    color: '#163828',
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 1,
    fontFamily: Fonts.sans,
  },
  input: {
    minHeight: 52,
    borderRadius: 14,
    borderWidth: 1.5,
    borderColor: '#E4E8E4',
    backgroundColor: '#FAFBFA',
    paddingHorizontal: 14,
    color: '#1C1C1E',
    fontSize: 15,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 4,
  },
  cancelBtn: {
    flex: 1,
    minHeight: 52,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F5F7F5',
    borderWidth: 1,
    borderColor: '#E6ECE6',
  },
  cancelBtnText: {
    color: '#3C4A3E',
    fontSize: 12,
    fontWeight: '800',
  },
  saveBtn: {
    flex: 2,
    minHeight: 52,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#163828',
  },
  saveBtnText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '800',
  },
  pressed: {
    opacity: 0.82,
    transform: [{ scale: 0.99 }],
  },
});
